// src/pages/DoctorsOverview/components/ClinicModal.js
import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Select, message, Button } from "antd";
import PhoneInput from "react-phone-input-2";
import { useDispatch, useSelector } from "react-redux";
import {
  selectSelectedClinic,
  selectIsEditClinic,
  selectLocations,
} from "../DoctorsSelectors";
import {
  createOrUpdateClinic,
  fetchClinicsDetails,
  fetchClinicsList,
  fetchLocations,
} from "../DoctorsThunk";
import { closeClinicModal } from "../DoctorsSlice";
import { getISOFromCountryName } from "../../../lib/currency";

const ClinicModal = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const selected = useSelector(selectSelectedClinic);
  const isEdit = useSelector(selectIsEditClinic);
  const locations = useSelector(selectLocations);

  const [phoneInputValue, setPhoneInputValue] = useState("");
  const [phoneDialCode, setPhoneDialCode] = useState("");

  const authInfo = useSelector((state) => state.auth.auth_info);

  useEffect(() => {
    dispatch(fetchLocations());
  }, [dispatch]);

  useEffect(() => {
    if (isEdit && selected) {
      const phone = selected.phone_no || "";
      const countryCode = selected.country_code
        ? String(selected.country_code)
        : "";
      const combined = countryCode ? `${countryCode}${phone}` : phone;
 
      form.setFieldsValue({
        clinic_name: selected.clinic_name || "",
        contact_name: selected.contact_name || "",
        phone: phone || "",
        country_code: countryCode || "",
        // location: selected.location || "",
        location:
          selected.location !== undefined && selected.location !== null
            ? selected.location
            : "",
      });

      setPhoneDialCode(countryCode);
      setPhoneInputValue(combined || "");
    } else {
      form.resetFields();
      setPhoneDialCode("");
      setPhoneInputValue("");
    }
  }, [selected, isEdit, form]);

  const handleCancel = () => {
    dispatch(closeClinicModal());
    form.resetFields();
    setPhoneDialCode("");
    setPhoneInputValue("");
  };

  const validatePhone = (_, value) => {
    const phone = value ? String(value).replace(/\D/g, "") : "";
    if (!phone) return Promise.reject(new Error("Contact number is required"));
    if (phone.length < 7 || phone.length > 15)
      return Promise.reject(
        new Error("Enter a valid contact number (7–15 digits)")
      );
    return Promise.resolve();
  };

  const onPhoneChange = (value, country) => {
    const digits = String(value || "").replace(/\D/g, "");
    const dial = country?.dialCode ? String(country.dialCode) : "";

    let local = digits;
    if (dial && local.startsWith(dial)) {
      local = local.slice(dial.length);
    }

    form.setFieldsValue({
      phone: local,
      country_code: dial,
    });

    setPhoneInputValue(digits);
    setPhoneDialCode(dial);
  };

  const handleFinish = async (values) => {
    const payload = {
      clinic_name: values.clinic_name,
      contact_name: values.contact_name,
      phone_no: values.phone ? String(values.phone).replace(/\D/g, "") : "",
      country_code: values.country_code || "",
      location: values.location || "",
      action_clinic: isEdit ? "update" : "create",
      clinic_edit_id: isEdit ? selected.clinic_id : undefined,
    };

    try {
      await dispatch(createOrUpdateClinic(payload)).unwrap();
      message.success("Clinic saved successfully");
      dispatch(closeClinicModal());
      dispatch(fetchClinicsList());
      dispatch(fetchClinicsDetails());
    } catch (err) {
      message.error(err?.message || "Save failed");
    }
  };

  const defaultCountry = getISOFromCountryName(authInfo?.country);

  return (
    <Modal
      title={isEdit ? "Update Clinic" : "New Clinic"}
      visible={true}
      onCancel={handleCancel}
      footer={null}
      destroyOnClose
    >
      <Form layout="vertical" form={form} onFinish={handleFinish}>
        <Form.Item
          name="clinic_name"
          label="Clinic Name"
           data-testid="clinic_name"
          rules={[{ required: true, message: "Clinic Name is required" }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="contact_name"
          label="Contact Name"
          data-testid="clinic_contact_name"
          rules={[{ required: true, message: "Contact Name is required" }]}
        >
          <Input />
        </Form.Item>

        <Form.Item label="Phone No" required>
          <PhoneInput
            country={defaultCountry}
            value={phoneInputValue}
            onChange={onPhoneChange}
            inputStyle={{
              width: "100%",
              height: "39.6px",
              background: "rgba(0,0,0,0.04)",
              borderColor: "transparent",
            }}
          />
          <Form.Item
            name="phone"
            noStyle
            rules={[{ validator: validatePhone }]}
          >
            <Input type="hidden" />
          </Form.Item>
          <Form.Item name="country_code" noStyle>
            <Input type="hidden" />
          </Form.Item>
        </Form.Item>

        <Form.Item
          name="location"
          label="Location"
          rules={[{ required: true, message: "Location is required" }]}
        >
          <Select
            showSearch
            options={(locations || []).map((l) => ({
              label: l.location_name,
              value: l.location_id,
            }))}
            placeholder="Select location"
            optionFilterProp="label"
          />
        </Form.Item>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 12,
            marginTop: 16,
          }}
        >
          <Button onClick={handleCancel}>Discard</Button>
          <Button type="primary" htmlType="submit"  data-testid="clinic_save">
            {isEdit ? "Update" : "Submit"}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default ClinicModal;
